import { Chip, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { get, isEmpty, isNil, map } from 'lodash'
import { useEffect, useState } from 'react'
import { FilterOptions, RequestHistoryFilters } from '../../../api/models'
import CustomDialog from '../../../components/dialogs/CustomDialog/CustomDialog'
import { FormFieldLabel } from '../../../components/form/FormFieldLabel'
import styles from './FiltersDialog.module.css'

interface FiltersDialogProps {
  open: boolean
  primaryAction: (filters: object) => Promise<void>
  primaryActionButtonText: string
  secondaryAction: () => void
  secondaryActionButtonText: string
  title: string
  filters: RequestHistoryFilters
  filterOptions: FilterOptions | null
}

const FiltersDialog = ({
  open,
  primaryAction,
  primaryActionButtonText,
  secondaryAction,
  secondaryActionButtonText,
  title,
  filters,
  filterOptions,
}: FiltersDialogProps) => {
  const [internalFilters, setInternalFilters] = useState<RequestHistoryFilters>(filters)

  useEffect(() => {
    setInternalFilters(filters)
  }, [filters])

  return (
    <CustomDialog
      open={open}
      primaryAction={async () => await primaryAction(internalFilters)}
      primaryActionButtonText={primaryActionButtonText}
      secondaryAction={() => {
        secondaryAction()
        setInternalFilters(filters)
      }}
      secondaryActionButtonText={secondaryActionButtonText}
      title={title}
      clearFilters={() => setInternalFilters({})}
      clearFiltersButtonText="Clear All"
      fullWidth>
      <div className={styles.filtersDialogContent}>
        <div>
          <FormFieldLabel text="Status" />
          <div className={styles.chipOptions}>
            <div className={styles.chipOptionsList}>
              <Chip
                label="Show All"
                className={isNil(get(internalFilters, 'status')) ? styles.chipOptionSelected : styles.chipOption}
                onClick={() => setInternalFilters({ ...internalFilters, status: undefined })}
              />
              <Chip
                key={'Declined'}
                label={'Declined'}
                className={
                  get(internalFilters, 'status') === 'rejected' ? styles.chipOptionSelected : styles.chipOption
                }
                onClick={() => setInternalFilters({ ...internalFilters, status: 'rejected' })}
              />
              <Chip
                key={'Approved'}
                label={'Approved'}
                className={
                  get(internalFilters, 'status') === 'approved' ? styles.chipOptionSelected : styles.chipOption
                }
                onClick={() => setInternalFilters({ ...internalFilters, status: 'approved' })}
              />
            </div>
          </div>
        </div>
        <div className={styles.selectOptionsContainer}>
          <div className={styles.selectOptionsItem}>
            <FormFieldLabel text="OU" />
            <FormControl
              fullWidth
              size="small">
              {isEmpty(get(internalFilters, 'ou')) && <InputLabel shrink={false}>Select</InputLabel>}
              <Select
                size="small"
                value={get(internalFilters, 'ou') ?? ''}
                onChange={(e: SelectChangeEvent) => {
                  setInternalFilters({
                    ...internalFilters,
                    ou: e.target.value,
                  })
                }}>
                <MenuItem
                  key={'no-ou'}
                  value={undefined}>
                  All
                </MenuItem>
                {map(filterOptions?.ous, option => (
                  <MenuItem
                    key={option}
                    value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        <div className={styles.selectOptionsContainer}>
          <div className={styles.selectOptionsItem}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormFieldLabel text="Start date" />
              <FormControl
                size="small"
                fullWidth>
                <DatePicker
                  className={styles.dateFormField}
                  value={
                    isNil(get(internalFilters, 'start_date')) ? null : dayjs.utc(get(internalFilters, 'start_date'))
                  }
                  onChange={value => {
                    setInternalFilters({ ...internalFilters, start_date: value?.toISOString() ?? undefined })
                  }}
                  slotProps={{
                    day: { classes: { selected: styles.selectedDay } },
                    yearButton: { className: styles.yearButton },
                    textField: {
                      size: 'small',
                      inputProps: {
                        className: styles.dateInput,
                      },
                    },
                  }}
                />
              </FormControl>
            </LocalizationProvider>
          </div>
          <div className={styles.selectOptionsItem}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormFieldLabel text="End date" />
              <FormControl
                size="small"
                fullWidth>
                <DatePicker
                  className={styles.dateFormField}
                  value={isNil(get(internalFilters, 'end_date')) ? null : dayjs.utc(get(internalFilters, 'end_date'))}
                  onChange={value => {
                    setInternalFilters({ ...internalFilters, end_date: value?.toISOString() ?? undefined })
                  }}
                  slotProps={{
                    day: { classes: { selected: styles.selectedDay } },
                    yearButton: { className: styles.yearButton },
                    textField: {
                      size: 'small',
                    },
                  }}
                />
              </FormControl>
            </LocalizationProvider>
          </div>
        </div>
        <div className={styles.selectOptionsItem}>
          <FormFieldLabel text="Processed by" />
          <FormControl
            fullWidth
            size="small">
            {isEmpty(get(internalFilters, 'processed_by_user_id')) && <InputLabel shrink={false}>Select</InputLabel>}
            <Select
              size="small"
              value={get(internalFilters, 'processed_by_user_id') ?? ''}
              onChange={(e: SelectChangeEvent) => {
                setInternalFilters({
                  ...internalFilters,
                  processed_by_user_id: e.target.value,
                })
              }}>
              <MenuItem
                key={'no-processed-by'}
                value={undefined}>
                All
              </MenuItem>
              {map(filterOptions?.approvers, option => (
                <MenuItem
                  key={option.id}
                  value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
    </CustomDialog>
  )
}

export default FiltersDialog
